import { CallService } from '../../interface';
import { getDefaultVideoQuality } from '../../../../shared/deviceCapabilities';
import { autoQualityAdjustorCpuMetrics } from './autoVideoQualityAdjusterMetrics';

/**
 * Automatically adjusts video quality based on consecutive CPU checks.
 *
 * - If poor conditions (high CPU) are detected for 3 consecutive intervals, quality is downgraded.
 *   - We then pause monitoring for 1 minute before checking if the CPU has improved.
 *   - If CPU hasn't improved - then we increase the time we pause monitoring for by a factor of 2 i.e. we now wait for 2 further minutes before checking conditions again.
 *   - This exponential back off keeps happening until such a time conditions improve (1min -> 2min -> 4min)
 * - If good conditions are sustained for 3 consecutive intervals after downgrade, quality is restored. The cooldown time stays the same and increases when the CPU is unstable again.
 * - If 3 downgrades have occurred, the adjustment is paused until whole reset (happens when the user sets quality manually) or new call is started.
 * - If the user sets quality manually, automatic adjustment is paused.
 */
export class AutoVideoQualityAdjuster {
  private readonly cpuUnstableStates = ['critical', 'serious'];
  private readonly requiredVotesToChange = 3;

  private readonly checkInterval = 4000;
  private readonly baseCooldown = 60_000; // 1 minute
  private readonly maxCooldown = 3600_000; // 1 hour
  private readonly cooldownMultiplier = 2; // exponential growth of cooldown (i.e. 1min -> 2min -> 4min -> 8min -> 16min...)

  private intervalId?: number;
  private unstableVotes = 0;
  private stableVotes = 0;

  private isDowngraded = false;
  private downgradeCount = 0;
  private readonly maxDowngradeCount = 3;
  private nextEligibleChangeAt: number | undefined;

  start(callService: CallService) {
    if (!this.isSupported()) {
      return;
    }

    // We need these stats until the start of the next call. Follow the same approach as Metric monitor which resets on next connect
    autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuDowngradeCount =
      undefined;
    autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuUpgradeCount =
      undefined;
    autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuRecovered = undefined;

    this.intervalId = setInterval(async () => {
      // If the user has manually set the video quality, we don't need to adjust it
      if (callService.manualVideoQuality) {
        this.reset();
        return;
      }

      if (this.downgradeCount >= this.maxDowngradeCount) {
        return;
      }

      const now = Date.now();

      // If the next eligible change is in the future, we don't need to check
      if (this.nextEligibleChangeAt && now < this.nextEligibleChangeAt) {
        return;
      }

      const cpu = callService.getCpuPerformanceState();
      const isCpuUnstable = cpu && this.cpuUnstableStates.includes(cpu);

      if (isCpuUnstable) {
        this.unstableVotes = Math.min(
          this.unstableVotes + 1,
          this.requiredVotesToChange,
        );
        this.stableVotes = 0;
      } else {
        this.stableVotes = Math.min(
          this.stableVotes + 1,
          this.requiredVotesToChange,
        );
        this.unstableVotes = 0;
      }

      // Handle downgrade
      if (this.unstableVotes >= this.requiredVotesToChange) {
        this.nextEligibleChangeAt = now + this.cooldown;
        this.downgradeCount++;

        if (!this.isDowngraded) {
          this.isDowngraded = true;

          callService.setVideoQuality('ld', 'unstable cpu');

          autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuDowngradeCount =
            (autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuDowngradeCount ??
              0) + 1;
          autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuRecovered = false;
        }

        this.resetVotes();
        // Handle restore - check if we have enough stable votes
      } else if (this.stableVotes >= this.requiredVotesToChange) {
        this.nextEligibleChangeAt = undefined;

        if (this.isDowngraded) {
          this.isDowngraded = false;
          callService.setVideoQuality(getDefaultVideoQuality(), 'stable cpu');

          autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuUpgradeCount =
            (autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuUpgradeCount ??
              0) + 1;
          autoQualityAdjustorCpuMetrics.autoQualityAdjustorCpuRecovered = true;
        }

        this.resetVotes();
      }
    }, this.checkInterval) as unknown as number;
  }

  stop() {
    clearInterval(this.intervalId);
    this.intervalId = undefined;
    this.reset();
  }

  private get cooldown(): number {
    const cooldown =
      this.baseCooldown *
      Math.pow(this.cooldownMultiplier, this.downgradeCount);

    return Math.min(cooldown, this.maxCooldown);
  }

  private isSupported(): boolean {
    return 'PressureObserver' in globalThis;
  }

  private reset(): void {
    this.resetVotes();
    this.downgradeCount = 0;
    this.nextEligibleChangeAt = undefined;
    this.isDowngraded = false;
  }

  private resetVotes(): void {
    this.unstableVotes = 0;
    this.stableVotes = 0;
  }
}
